/**
 * BulkCvUploadButton — shared by /recruitment (free pool) and
 * /recruitment/positions/{pid} (auto-assigned to that role).
 *
 * Drops 10+ CVs at once → for each: POST /api/uploads → POST
 * /api/recruitment/candidates {upload_id, position_ids}. The backend
 * already runs OCR + ranks against any linked positions.
 *
 * Pass `positionId` to auto-assign every uploaded CV to that role; omit
 * it for a free-pool upload that the user assigns later.
 *
 * Live progress per row: queued / uploading / OCR / linked / failed.
 * Mobile contract: bottom-sheet drawer at 360px.
 */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Upload, Loader2, CheckCircle2, AlertCircle, FileText,
} from "lucide-react";
import api from "../../lib/api";

export default function BulkCvUploadButton({
  positionId = null,
  onDone,
  label = "Upload CVs",
  variant = "primary", // "primary" | "ghost"
}) {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);

  const pick = () => fileRef.current?.click();

  const onPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (fileRef.current) fileRef.current.value = "";
    setOpen(true);
    const initial = files.map((f) => ({
      key: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 7)}`,
      name: f.name,
      size: f.size,
      status: "queued",
      error: null,
      candidateId: null,
      score: null,
      parsedName: null,
    }));
    setRows((prev) => [...prev, ...initial]);
    setRunning(true);
    for (let i = 0; i < initial.length; i += 1) {
      const file = files[i];
      const key = initial[i].key;
      try {
        setRows((rs) => rs.map((r) => (r.key === key ? { ...r, status: "uploading" } : r)));
        const fd = new FormData();
        fd.append("file", file);
        fd.append("source", "recruitment");
        const upResp = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 0,  // background upload — no wall-clock cap (see uploadManager)
        });
        const uploadId = upResp.data?.id || upResp.data?.upload_id;
        if (!uploadId) throw new Error("Upload didn't return an id");
        // 2026-02 — OCR now runs in the background server-side. We get
        // an immediate response with a stub candidate (ocr_status=
        // "pending"). The FE marks the row "scanning" and the user can
        // move on — the candidate page picks up the parsed fields once
        // OCR completes. Massively faster bulk UX.
        setRows((rs) => rs.map((r) => (r.key === key ? { ...r, status: "scanning" } : r)));
        const candResp = await api.post(
          "/recruitment/candidates",
          {
            upload_id: uploadId,
            position_ids: positionId ? [positionId] : [],
          },
          { timeout: 30_000 },
        );
        const cand = candResp.data || {};
        const ranking = positionId
          ? (cand.rankings || {})[positionId] || {}
          : {};
        setRows((rs) => rs.map((r) => (
          r.key === key
            ? {
                ...r,
                status: "linked",
                candidateId: cand.id,
                score: typeof ranking.score === "number" ? ranking.score : null,
                parsedName: cand.full_name,
              }
            : r
        )));
      } catch (err) {
        // Treat ANY error as retryable — don't permanently fail the row.
        const msg = err?.response?.data?.detail || err?.message || "Failed";
        setRows((rs) => rs.map((r) => (
          r.key === key
            ? { ...r, status: "failed", error: String(msg).slice(0, 240), retryFile: file }
            : r
        )));
      }
    }
    setRunning(false);
    onDone?.();
  };

  const retryFailed = async () => {
    const failed = rows.filter((r) => r.status === "failed" && r.retryFile);
    if (failed.length === 0) return;
    setRunning(true);
    for (const r of failed) {
      const key = r.key;
      try {
        setRows((rs) => rs.map((x) => (x.key === key ? { ...x, status: "uploading", error: null } : x)));
        const fd = new FormData();
        fd.append("file", r.retryFile);
        fd.append("source", "recruitment");
        const upResp = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 0,  // background upload — no wall-clock cap (see uploadManager)
        });
        const uploadId = upResp.data?.id || upResp.data?.upload_id;
        setRows((rs) => rs.map((x) => (x.key === key ? { ...x, status: "scanning" } : x)));
        const candResp = await api.post(
          "/recruitment/candidates",
          { upload_id: uploadId, position_ids: positionId ? [positionId] : [] },
          { timeout: 30_000 },
        );
        const cand = candResp.data || {};
        const ranking = positionId ? (cand.rankings || {})[positionId] || {} : {};
        setRows((rs) => rs.map((x) => (
          x.key === key
            ? { ...x, status: "linked", candidateId: cand.id, score: typeof ranking.score === "number" ? ranking.score : null, parsedName: cand.full_name }
            : x
        )));
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Failed";
        setRows((rs) => rs.map((x) => (x.key === key ? { ...x, status: "failed", error: String(msg).slice(0, 240) } : x)));
      }
    }
    setRunning(false);
    onDone?.();
  };

  const clear = () => {
    if (running) return;
    setRows([]);
    setOpen(false);
  };

  const linked = rows.filter((r) => r.status === "linked").length;
  const failed = rows.filter((r) => r.status === "failed").length;
  const failedRetryable = rows.filter((r) => r.status === "failed" && r.retryFile).length;
  const btnClass = variant === "primary" ? "hv-btn hv-btn-primary text-xs" : "hv-btn hv-btn-ghost text-xs";

  return (
    <>
      <button
        type="button"
        onClick={pick}
        className={btnClass}
        data-testid={positionId ? "position-bulk-cv-btn" : "recruit-bulk-cv-btn"}
      >
        <Upload className="w-3.5 h-3.5" /> {label}
      </button>
      <input
        ref={fileRef}
        type="file"
        multiple
        accept="application/pdf,image/*,.doc,.docx"
        className="hidden"
        onChange={onPicked}
        data-testid={positionId ? "position-bulk-cv-input" : "recruit-bulk-cv-input"}
      />
      {open && rows.length > 0 && (
        <div
          className="fixed inset-0 z-[1100] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 hv-safe-top hv-safe-bottom"
          data-testid="bulk-cv-progress-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !running) clear(); }}
        >
          <div className="w-full max-w-lg bg-[#121419] border border-white/10 rounded-t-2xl sm:rounded-lg overflow-hidden flex flex-col max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom))]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-wrap gap-2">
              <h3 className="font-display text-base font-bold inline-flex items-center gap-2">
                <Upload className="w-4 h-4 text-[#1FB6C1]" /> Bulk CV upload
              </h3>
              <span className="text-[10px] text-[#647087] font-mono">
                {linked} linked · {failed} failed · {rows.length} total
              </span>
              <button
                onClick={clear}
                disabled={running}
                className="text-[#647087] hover:text-white disabled:opacity-30 ml-auto"
                data-testid="bulk-cv-close"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {rows.map((r) => (
                <BulkCvRow key={r.key} row={r} positionId={positionId} />
              ))}
            </div>
            <div className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between flex-wrap gap-2">
              <span className="text-[11px] text-[#647087]">
                {running
                  ? (positionId
                      ? "Uploading… CVs are saved instantly; OCR + ranking runs in the background."
                      : "Uploading… CVs are saved instantly; OCR runs in the background.")
                  : "Done. Close this and the candidate list will refresh. OCR results stream in over the next minute or two."}
              </span>
              <div className="flex items-center gap-2">
                {failedRetryable > 0 && !running && (
                  <button
                    type="button"
                    onClick={retryFailed}
                    className="hv-btn hv-btn-ghost text-xs"
                    data-testid="bulk-cv-retry-failed"
                  >
                    <Upload className="w-3 h-3" /> Retry {failedRetryable} failed
                  </button>
                )}
                <button
                  type="button"
                  onClick={pick}
                  disabled={running}
                  className="hv-btn hv-btn-ghost text-xs disabled:opacity-40"
                  data-testid="bulk-cv-add-more"
                >
                  <Upload className="w-3 h-3" /> Add more
                </button>
                <button
                  type="button"
                  onClick={clear}
                  disabled={running}
                  className="hv-btn hv-btn-primary text-xs disabled:opacity-40"
                  data-testid="bulk-cv-done"
                >
                  {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function BulkCvRow({ row, positionId }) {
  const STATUS_LABEL = {
    queued:    { txt: "Queued",     icon: <Loader2 className="w-3 h-3" />,                  color: "text-[#647087]" },
    uploading: { txt: "Uploading…", icon: <Loader2 className="w-3 h-3 animate-spin" />,     color: "text-[#1FB6C1]" },
    scanning:  { txt: "Scanning in background…", icon: <Loader2 className="w-3 h-3 animate-spin" />, color: "text-[#A78BFA]" },
    ocr:       { txt: "OCR…",       icon: <Loader2 className="w-3 h-3 animate-spin" />,     color: "text-[#A78BFA]" },
    linked:    { txt: positionId ? "Linked" : "Saved", icon: <CheckCircle2 className="w-3 h-3" />, color: "text-emerald-300" },
    failed:    { txt: "Failed",     icon: <AlertCircle className="w-3 h-3" />,              color: "text-rose-300" },
  };
  const s = STATUS_LABEL[row.status] || STATUS_LABEL.queued;
  return (
    <div
      className="flex items-center gap-2 bg-white/[0.02] rounded px-2 py-2 text-xs"
      data-testid={`bulk-cv-row-${row.key}`}
    >
      <FileText className="w-3.5 h-3.5 text-[#a0aabf] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="truncate text-[#dde3ec]" title={row.name}>
          {row.name}
        </div>
        <div className="text-[10px] text-[#647087] flex items-center gap-1.5 flex-wrap">
          <span className={`inline-flex items-center gap-1 ${s.color}`}>{s.icon} {s.txt}</span>
          {row.parsedName && row.status === "linked" && (
            <span className="text-[#a0aabf]">· {row.parsedName}</span>
          )}
          {typeof row.score === "number" && (
            <span className="text-emerald-200">· score {row.score}</span>
          )}
          {row.error && <span className="text-rose-300">· {row.error}</span>}
        </div>
      </div>
      {row.status === "linked" && row.candidateId && (
        <Link
          to={`/recruitment/candidates/${row.candidateId}`}
          className="text-[#1FB6C1] hover:underline text-[11px] shrink-0"
          data-testid={`bulk-cv-row-${row.key}-open`}
        >
          Open
        </Link>
      )}
    </div>
  );
}
